import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  DatasourceInstructionMode,
  type DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useDealNotEditableHandler } from '@/lib/hooks/useDealNotEditableHandler';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { trackEvent } from '@/lib/mixpanel';
import {
  ColumnFilterId,
  columnFiltersState,
} from '@/states/column-filters.state';
import {
  columnSortState,
  tableSortResetSignalState,
} from '@/states/column-sort.state';
import {
  DatasourceImportReportCategory,
  type DatasourceImportReport,
  datasourceImportReportState,
} from '@/states/datasource-import-report.state';
import { displayModeState } from '@/states/display-mode.state';
import { productsState } from '@/states/products.state';
import {
  type SelectionModel,
  selectedProductIdsState,
  rowSelectionModelState,
} from '@/states/row-selection.state';
import { DisplayMode } from '@/types/display-mode';
import type { ColumnFiltersState, ColumnSort } from '@tanstack/react-table';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

type ImportReportTab = {
  type: DatasourceImportReportCategory;
  ids: string[];
  count: number;
  displayMode: DisplayMode;
};

type TableSnapshot = {
  columnFilters: ColumnFiltersState;
  columnSort: ColumnSort[];
  displayMode: DisplayMode;
  rowSelection: SelectionModel;
};

type DatasourceImportReportOverlayProps = {
  dealId: string;
};

const EMPTY_IMPORT_REPORT_FILTER = ['__empty_import_report_category__'];

function splitSkippedIds(
  ids: string[],
  productsById: Map<string, DealItemWithVersions>
) {
  return ids.reduce<{ selected: string[]; excluded: string[] }>(
    (acc, id) => {
      const product = productsById.get(id);
      if (!product || product.activeVersion?.isSelected) {
        acc.selected.push(id);
      } else {
        acc.excluded.push(id);
      }
      return acc;
    },
    { selected: [], excluded: [] }
  );
}

function buildTabs(
  report: NonNullable<DatasourceImportReport>,
  productsById: Map<string, DealItemWithVersions>
): ImportReportTab[] {
  const skipped = splitSkippedIds(report.itemsSkippedIds, productsById);

  const buildTab = (
    type: DatasourceImportReportCategory,
    ids: string[],
    displayMode: DisplayMode
  ): ImportReportTab => ({
    type,
    ids,
    count: new Set(ids).size,
    displayMode,
  });

  if (report.instructionMode === DatasourceInstructionMode.UpdateOnly) {
    return [
      buildTab(
        'selectedUpdated',
        report.selectedItemsUpdatedIds,
        DisplayMode.SELECTED
      ),
      buildTab(
        'excludedUpdated',
        report.excludedItemsUpdatedIds,
        DisplayMode.DESELECTED
      ),
    ];
  }

  if (report.instructionMode === DatasourceInstructionMode.CreateOnly) {
    return [
      buildTab('created', report.itemsCreatedIds, DisplayMode.SELECTED),
      buildTab('skippedSelected', skipped.selected, DisplayMode.SELECTED),
      buildTab('skippedExcluded', skipped.excluded, DisplayMode.DESELECTED),
    ];
  }

  return [
    buildTab('created', report.itemsCreatedIds, DisplayMode.SELECTED),
    buildTab(
      'selectedUpdated',
      report.selectedItemsUpdatedIds,
      DisplayMode.SELECTED
    ),
    buildTab(
      'excludedUpdated',
      report.excludedItemsUpdatedIds,
      DisplayMode.DESELECTED
    ),
    buildTab('skippedSelected', skipped.selected, DisplayMode.SELECTED),
    buildTab('skippedExcluded', skipped.excluded, DisplayMode.DESELECTED),
  ];
}

function updateFilter(
  filters: ColumnFiltersState,
  columnId: ColumnFilterId,
  value: unknown
): ColumnFiltersState {
  const filterExists = filters.some((filter) => filter.id === columnId);
  if (!filterExists) return [...filters, { id: columnId, value }];

  return filters.map((filter) =>
    filter.id === columnId ? { ...filter, value } : filter
  );
}

export function DatasourceImportReportOverlay({
  dealId,
}: DatasourceImportReportOverlayProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [report, setReport] = useAtom(datasourceImportReportState);
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersState);
  const [columnSort, setColumnSort] = useAtom(columnSortState);
  const [displayMode, setDisplayMode] = useAtom(displayModeState);
  const [rowSelection, setRowSelection] = useAtom(rowSelectionModelState);
  const setTableSortResetSignal = useSetAtom(tableSortResetSignalState);
  const selectedProductIds = useAtomValue(selectedProductIdsState);
  const products = useAtomValue(productsState);
  const { updateProducts, loading } = useUpdateDealItems(dealId);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const [activeTabType, setActiveTabType] =
    useState<DatasourceImportReportCategory | null>(null);
  const [submittingReinclude, setSubmittingReinclude] = useState(false);
  const snapshotRef = useRef<TableSnapshot | null>(null);

  const productsById = useMemo(() => {
    const map = new Map<string, DealItemWithVersions>();
    for (const product of products) {
      map.set(product.id, product);
    }
    return map;
  }, [products]);

  const tabs = useMemo(
    () => (report ? buildTabs(report, productsById) : []),
    [productsById, report]
  );

  const activeTab =
    tabs.find((tab) => tab.type === activeTabType) ?? tabs[0] ?? null;

  useEffect(() => {
    if (report && report.dealId !== dealId) {
      setReport(null);
    }
  }, [dealId, report, setReport]);

  useEffect(() => {
    if (!report || activeTabType) return;
    setActiveTabType(tabs[0].type);
  }, [activeTabType, report, tabs]);

  useEffect(() => {
    if (!report || snapshotRef.current) return;
    snapshotRef.current = {
      columnFilters,
      columnSort,
      displayMode,
      rowSelection,
    };
  }, [columnFilters, columnSort, displayMode, report, rowSelection]);

  useEffect(() => {
    if (!report || !activeTab) return;

    setColumnFilters((previous) =>
      updateFilter(
        updateFilter(
          previous,
          ColumnFilterId.__comparisonFilter,
          activeTab.ids.length > 0 ? activeTab.ids : EMPTY_IMPORT_REPORT_FILTER
        ),
        ColumnFilterId.__displayMode,
        activeTab.displayMode
      )
    );
    setDisplayMode(activeTab.displayMode);

    setRowSelection({ mode: 'none' });
  }, [activeTab, report, setColumnFilters, setDisplayMode, setRowSelection]);

  const restoreSnapshot = useCallback(() => {
    const snapshot = snapshotRef.current;
    if (snapshot) {
      setColumnFilters(snapshot.columnFilters);
      setColumnSort(snapshot.columnSort);
      setDisplayMode(snapshot.displayMode);
      setRowSelection(snapshot.rowSelection);
      snapshotRef.current = null;
    }
  }, [setColumnFilters, setColumnSort, setDisplayMode, setRowSelection]);

  useEffect(() => restoreSnapshot, [restoreSnapshot]);

  const closeReport = useCallback(() => {
    restoreSnapshot();
    setTableSortResetSignal((value) => value + 1);
    setActiveTabType(null);
    setReport(null);
  }, [restoreSnapshot, setReport, setTableSortResetSignal]);

  const canReinclude =
    activeTab?.type === 'excludedUpdated' ||
    activeTab?.type === 'skippedExcluded';
  const reincludeBusy = loading || submittingReinclude;
  const selectedActionIds = useMemo(() => {
    if (!activeTab || !canReinclude) return [];
    const activeTabIdsSet = new Set(activeTab.ids);
    return selectedProductIds.filter((id) => activeTabIdsSet.has(id));
  }, [activeTab, canReinclude, selectedProductIds]);

  const handleTabClick = (tab: ImportReportTab) => {
    trackEvent('IMPORT_REPORT_TAB_SELECTED', {
      dealId,
      tabType: tab.type,
    });
    setActiveTabType(tab.type);
  };

  const handleReinclude = async () => {
    if (!activeTab || !canReinclude || selectedActionIds.length === 0) return;

    trackEvent('IMPORT_REPORT_REINCLUDE_CLICKED', {
      dealId,
      tabType: activeTab.type,
    });
    setSubmittingReinclude(true);
    try {
      const result = await updateProducts(
        selectedActionIds.map((id) => ({ id, isSelected: true }))
      );
      const response = result.data?.updateDealItemsV2;
      if (response?.__typename === 'DomainException') {
        trackEvent('IMPORT_REPORT_REINCLUDE_FAILED', {
          dealId,
          tabType: activeTab.type,
          errorCode: response.code,
        });
        if (handleIfDealNotEditable(response)) {
          closeReport();
          return;
        }
        toast({
          title: t('general.error'),
          description: t('general.error_cannot_save_modifications'),
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: t('general.success'),
        description: t(
          'components.deal_items_subscription.import_report_reinclude_success'
        ),
        duration: 3000,
      });
      trackEvent('IMPORT_REPORT_REINCLUDE_SUCCEEDED', {
        dealId,
        tabType: activeTab.type,
      });
      setReport((currentReport) => {
        if (!currentReport) return currentReport;

        const reincludeIds = new Set(selectedActionIds);

        return {
          ...currentReport,
          selectedItemsUpdatedIds: [
            ...currentReport.selectedItemsUpdatedIds,
            ...reincludeIds,
          ],
          excludedItemsUpdatedIds: currentReport.excludedItemsUpdatedIds.filter(
            (id) => !reincludeIds.has(id)
          ),
        };
      });
      setTableSortResetSignal((value) => value + 1);
    } catch (error) {
      console.error('reinclude import report products:', error);
      trackEvent('IMPORT_REPORT_REINCLUDE_FAILED', {
        dealId,
        tabType: activeTab.type,
        errorCode: 'network_error',
      });
      toast({
        title: t('general.error'),
        description: t('general.error_network_message'),
        variant: 'destructive',
      });
    } finally {
      setSubmittingReinclude(false);
    }
  };

  if (!report || !activeTab) return null;

  return (
    <div className="absolute inset-x-0 bottom-3 z-modal px-4">
      <section className="rounded-md border border-primary/20 bg-white/95 p-3 shadow-lg backdrop-blur-sm">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,0.85fr)_minmax(420px,1.4fr)]">
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('components.deal_items_subscription.import_report_kicker')}
            </p>
            <h2 className="truncate text-base font-semibold">
              {t(
                `components.deal_items_subscription.import_report_${activeTab.type}_label`
              )}
            </h2>
            <p className="max-h-10 overflow-hidden text-sm leading-5 text-muted-foreground">
              {t(
                `components.deal_items_subscription.import_report_${activeTab.type}_description`
              )}
            </p>
          </div>

          <div className="flex flex-row gap-2">
            <div
              aria-disabled="true"
              className="pointer-events-none h-16 w-auto cursor-default rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2 text-left text-muted-foreground"
            >
              <span className="block truncate text-xs font-medium">
                {t(
                  'components.deal_items_subscription.import_report_total_label'
                )}
              </span>
              <span className="mt-1 block text-base font-semibold">
                {tabs.reduce((acc, curr) => acc + curr.count, 0)}
              </span>
            </div>
            <div className="grid w-full grid-cols-3 gap-2">
              {tabs.map((tab) => {
                const selected = tab.type === activeTab.type;
                return (
                  <button
                    key={tab.type}
                    type="button"
                    onClick={() => handleTabClick(tab)}
                    className={`col-span-1 h-16 min-w-0 rounded-md border px-3 py-2 text-left transition-colors ${
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-white hover:bg-muted/60'
                    }`}
                  >
                    <span className="block truncate text-xs font-medium">
                      {t(
                        `components.deal_items_subscription.import_report_${tab.type}_label`
                      )}
                    </span>
                    <span className="mt-1 block text-base font-semibold">
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-end gap-3">
          <Button variant="outline" size="sm" onClick={closeReport}>
            {t('components.deal_items_subscription.import_report_close')}
          </Button>
          <div className="flex justify-end">
            {canReinclude ? (
              <Button
                size="sm"
                className="gap-2"
                onClick={handleReinclude}
                disabled={reincludeBusy || selectedActionIds.length === 0}
              >
                {reincludeBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                {t(
                  'components.deal_items_subscription.import_report_reinclude'
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
